import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

const yellow = {
  main: '#FFD700',
  light: '#FFE44D',
  dark: '#FFC000',
  contrastText: '#000000',
};

const black = {
  main: '#000000',
  light: '#2C2C2C',
  dark: '#000000',
  contrastText: '#FFFFFF',
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: yellow,
    secondary: black,
    background: {
      default: '#1A1A1A',
      paper: '#2C2C2C',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
    },
    warning: yellow,
    error: {
      main: '#FF4444',
      light: '#FF6B6B',
      dark: '#CC0000',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#00C853',
      light: '#69F0AE',
      dark: '#00E676',
      contrastText: '#000000',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
          scrollbarColor: "#ffd700 #121212",
          "&::-webkit-scrollbar": {
            width: 8,
            backgroundColor: "#121212"
          },
          "&::-webkit-scrollbar-thumb": {
            background: `linear-gradient(135deg, ${alpha('#ffd700', 0.3)} 0%, ${alpha('#fff171', 0.3)} 100%)`,
            borderRadius: 4,
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha('#ffd700', 0.5)} 0%, ${alpha('#fff171', 0.5)} 100%)`
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          backgroundImage: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha('#ffd700', 0.15)}`
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#000000',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#000000',
          borderRight: '1px solid #2C2C2C',
          '& .MuiListItemButton-root': {
            margin: '4px 8px',
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: yellow.main,
              color: yellow.contrastText,
              '&:hover': {
                backgroundColor: yellow.dark,
              },
              '& .MuiListItemIcon-root': {
                color: yellow.contrastText,
              },
            },
            '&:hover': {
              backgroundColor: alpha('#ffd700', 0.08),
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #ffd700 0%, #fff171 100%)',
          color: '#000000',
          boxShadow: `0 2px 8px ${alpha('#ffd700', 0.3)}`,
          '&:hover': {
            background: 'linear-gradient(135deg, #fff171 0%, #ffd700 100%)',
            boxShadow: `0 4px 12px ${alpha('#ffd700', 0.4)}`,
            transform: 'translateY(-1px)'
          }
        },
        outlined: {
          borderColor: '#ffd700',
          color: '#ffd700',
          '&:hover': {
            borderColor: '#fff171',
            backgroundColor: alpha('#ffd700', 0.08),
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          borderRadius: 12,
          boxShadow: `0 2px 8px ${alpha('#000000', 0.2)}`,
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha('#ffd700', 0.15)}`
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha('#ffd700', 0.1)}`,
          color: '#ffffff'
        },
        head: {
          color: '#ffd700',
          fontWeight: 600,
          backgroundColor: alpha('#ffd700', 0.03)
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&.MuiChip-outlined': {
            borderColor: alpha('#ffd700', 0.5),
            '&:hover': {
              borderColor: '#ffd700',
              boxShadow: `0 2px 4px ${alpha('#ffd700', 0.2)}`
            }
          }
        },
        filled: {
          backgroundColor: alpha('#ffd700', 0.1),
          '&:hover': {
            backgroundColor: alpha('#ffd700', 0.2)
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#B0B0B0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: alpha('#ffd700', 0.3),
            },
            '&:hover fieldset': {
              borderColor: alpha('#ffd700', 0.5),
            },
            '&.Mui-focused fieldset': {
              borderColor: '#ffd700',
            }
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha('#000000', 0.9),
          color: '#ffffff',
          fontSize: '0.75rem',
          borderRadius: 4,
          boxShadow: `0 2px 8px ${alpha('#000000', 0.2)}`
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha('#ffd700', 0.1),
        },
        bar: {
          borderRadius: 4,
        }
      }
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#ffffff'
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#ffffff'
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#ffffff'
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#ffffff'
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#ffffff'
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#ffffff'
    },
    subtitle1: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    body1: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    caption: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      color: '#ffd700',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    }
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    `0 2px 4px ${alpha('#000000', 0.2)}`,
    `0 4px 8px ${alpha('#000000', 0.2)}`,
    `0 8px 16px ${alpha('#000000', 0.2)}`,
    `0 12px 24px ${alpha('#000000', 0.2)}`,
    `0 16px 32px ${alpha('#000000', 0.2)}`,
    `0 20px 40px ${alpha('#000000', 0.2)}`,
    `0 24px 48px ${alpha('#000000', 0.2)}`,
    `0 28px 56px ${alpha('#000000', 0.2)}`,
    `0 32px 64px ${alpha('#000000', 0.2)}`,
    `0 36px 72px ${alpha('#000000', 0.2)}`,
    `0 40px 80px ${alpha('#000000', 0.2)}`,
    `0 44px 88px ${alpha('#000000', 0.2)}`,
    `0 48px 96px ${alpha('#000000', 0.2)}`,
    `0 52px 104px ${alpha('#000000', 0.2)}`,
    `0 56px 112px ${alpha('#000000', 0.2)}`,
    `0 60px 120px ${alpha('#000000', 0.2)}`,
    `0 64px 128px ${alpha('#000000', 0.2)}`,
    `0 68px 136px ${alpha('#000000', 0.2)}`,
    `0 72px 144px ${alpha('#000000', 0.2)}`,
    `0 76px 152px ${alpha('#000000', 0.2)}`,
    `0 80px 160px ${alpha('#000000', 0.2)}`,
    `0 84px 168px ${alpha('#000000', 0.2)}`,
    `0 88px 176px ${alpha('#000000', 0.2)}`,
    `0 92px 184px ${alpha('#000000', 0.2)}`
  ],
}); 